import { useId } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { EyeOff } from "@acto/web-common/components";
import type { GlobalTokens } from "@acto/web-common/theme";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const collapsedFrame = (theme: GlobalTokens) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column" as const,
  gap: theme.fieldSalesTwo.spacing[4],
  boxSizing: "border-box" as const,
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius["32"],
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
});

const Frame = styled("div")(({ theme }) => collapsedFrame(theme));

const UnhideFrame = styled("button")(({ theme }) => ({
  ...collapsedFrame(theme),
  margin: 0,
  appearance: "none",
  WebkitAppearance: "none",
  textAlign: "left",
  cursor: "pointer",
  font: "inherit",
  transition: "transform 120ms ease, background-color 120ms ease",
  "@media (hover: hover)": {
    "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
  },
  ...pressInteraction(theme),
}));

const TitleRow = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[24],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": { width: "24px", height: "24px", flexShrink: 0 },
}));

const Label = styled("span")<{ faded?: boolean }>(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["520"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  variants: [
    {
      props: { faded: true },
      style: {
        backgroundImage: `linear-gradient(90deg, ${theme.fieldSalesTwo.colors.neutral[700]} 0%, ${theme.fieldSalesTwo.colors.neutral[800]} 12%, rgba(19,19,19,0.44) 20%, ${theme.fieldSalesTwo.colors.neutral[800]} 30%, rgba(19,19,19,0.44) 43%)`,
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        color: "transparent",
      },
    },
  ],
}));

const Hint = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["400"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.fieldSalesTwo.spacing[24],
  paddingTop: theme.fieldSalesTwo.spacing[8],
}));

const ActionButton = styled("button")<{ emphasis: "primary" | "secondary" }>(({ theme }) => ({
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  whiteSpace: "nowrap",
  transition: "transform 120ms ease",
  ...pressInteraction(theme),
  variants: [
    { props: { emphasis: "primary" }, style: { color: theme.fieldSalesTwo.colors.accent.action } },
    { props: { emphasis: "secondary" }, style: { color: theme.fieldSalesTwo.colors.neutral[900] } },
  ],
}));

interface HiddenCardLearnedRuleProps {
  cardLabel: string;
  hint: string;
  onAlwaysShow: () => void;
  onShowOnce: () => void;
}

export function HiddenCardLearnedRule({ cardLabel, hint, onAlwaysShow, onShowOnce }: HiddenCardLearnedRuleProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");
  const labelId = useId();

  return (
    <Frame role="group" aria-labelledby={labelId}>
      <TitleRow>
        <Label id={labelId} faded>
          {cardLabel}
        </Label>
        <EyeOff aria-hidden="true" />
      </TitleRow>
      <Hint>{hint}</Hint>
      <Actions>
        <ActionButton type="button" emphasis="primary" onClick={onAlwaysShow}>
          {t("alwaysShow")}
        </ActionButton>
        <ActionButton type="button" emphasis="secondary" onClick={onShowOnce}>
          {t("showOnce")}
        </ActionButton>
      </Actions>
    </Frame>
  );
}

interface HiddenCardOneOffProps {
  cardLabel: string;
  onUnhide: () => void;
}

export function HiddenCardOneOff({ cardLabel, onUnhide }: HiddenCardOneOffProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");

  return (
    <UnhideFrame type="button" aria-label={t("unhide", { label: cardLabel })} onClick={onUnhide}>
      <TitleRow>
        <Label>{cardLabel}</Label>
        <EyeOff aria-hidden="true" />
      </TitleRow>
    </UnhideFrame>
  );
}
